import React, { useState } from 'react';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SIMULATOR } from '@/constants/testIds';
import { Loader2, TrendingUp } from 'lucide-react';

const LEVERS = [
  { key: 'reviews', label: 'New reviews collected', max: 30, testid: SIMULATOR.reviewsSlider },
  { key: 'podcasts', label: 'Podcast appearances', max: 25, testid: SIMULATOR.podcastsSlider },
  { key: 'backlinks', label: 'Quality backlinks', max: 30, testid: SIMULATOR.backlinksSlider },
  { key: 'content_freshness', label: 'New articles / FAQs', max: 30 },
  { key: 'schema', label: 'Schema improvements', max: 25 },
  { key: 'media', label: 'Media mentions', max: 25 },
];

export default function Simulator() {
  const { activeClient } = useClient();
  const [deltas, setDeltas] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!activeClient) return <div className="text-sm text-muted-foreground">Select a client.</div>;

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/simulate', { client_id: activeClient.client_id, deltas });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Authority Simulator™</div>
        <h1 className="mt-2 font-display text-3xl tracking-tight">What-if modeling</h1>
        <p className="mt-2 text-sm text-muted-foreground">Project how authority moves when you pull specific levers.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          {LEVERS.map(lv => (
            <div key={lv.key}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{lv.label}</div>
                <div className="score-num text-sm text-primary">+{deltas[lv.key] || 0}</div>
              </div>
              <Slider
                data-testid={lv.testid}
                className="mt-3"
                min={0}
                max={lv.max}
                step={1}
                value={[deltas[lv.key] || 0]}
                onValueChange={(v) => setDeltas(d => ({ ...d, [lv.key]: v[0] }))}
              />
            </div>
          ))}
          <Button data-testid={SIMULATOR.runBtn} onClick={run} disabled={loading} className="w-full">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-1" />}
            Run simulation
          </Button>
        </div>

        <div data-testid={SIMULATOR.resultCard} className="rounded-xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Projected impact</div>
          {!result ? (
            <div className="mt-6 text-sm text-muted-foreground">Move the levers and run a simulation.</div>
          ) : (
            <>
              <div className="mt-4 flex items-end gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">Baseline</div>
                  <div className="score-num text-4xl">{result.baseline?.authority_score}</div>
                </div>
                <div className="text-2xl text-muted-foreground pb-2">→</div>
                <div>
                  <div className="text-xs text-muted-foreground">Projected</div>
                  <div className="score-num text-5xl text-primary">{result.projected?.authority_score}</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Badge>{result.projected?.letter_grade}</Badge>
                <Badge variant="outline">{result.projected?.certification}</Badge>
                <Badge variant="secondary">+{result.delta} pts</Badge>
              </div>
              <div className="mt-6 text-xs text-muted-foreground leading-relaxed">
                Deep Reasoning projects this configuration will increase your Authority Score by{' '}
                <span className="text-foreground font-medium">{result.delta} points</span>, lifting your certification
                tier to <span className="text-foreground font-medium">{result.projected?.certification}</span>.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
